import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import Image from 'components/Common/Image'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import focusStyle from 'styles/utils/focusStyle'
import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import styled from 'styled-components'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'

const PickItem = styled(Clickable)`
  display: grid;
  grid-template-columns: ${rem(100)} 1fr;
  height: ${rem(100)};
  gap: ${rem(16)};
  align-items: center;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  transition: background-color 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    }
  }
  &:focus-visible {
    background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
    ${focusStyle}
  }
`

const ItemImage = styled(Image)`
  border-radius: ${props => props.theme.borderRadius.S};
`

function EditorPickPriceDisplay({ price, ticketUnitLabel }: { price: number; ticketUnitLabel?: string }) {
  if (ticketUnitLabel) {
    return <FormattedMessage
      id="xqZxIcVqWsSLc-pYRYSY"
      defaultMessage="<caption>From</caption> <price>{formattedPrice}</price><caption>/{unit}</caption>"
      description="Experience price with from prefix and per-unit suffix"
      values={{
        caption: (chunks: React.ReactNode) => <Caption as="span" variant="large" colour="neutral-three">{chunks}</Caption>,
        price: (chunks: React.ReactNode) => <Heading as="span" variant="heading6">{chunks}</Heading>,
        formattedPrice: <FormatCurrency value={price} />,
        unit: ticketUnitLabel,
      }}
    />
  }
  return <FormattedMessage
    id="horP1U-q8T_J65MaoG0c"
    defaultMessage="<caption>From</caption> <price>{formattedPrice}</price>"
    description="Experience price with from prefix on the experience offer or booking page"
    values={{
      caption: (chunks: React.ReactNode) => <Caption as="span" variant="large" colour="neutral-three">{chunks}</Caption>,
      price: (chunks: React.ReactNode) => <Heading as="span" variant="heading6">{chunks}</Heading>,
      formattedPrice: <FormatCurrency value={price} />,
    }}
  />
}

interface Props {
  place: App.Place
}

function ExperiencesPageEditorsPickList(props: Props) {
  const { place } = props

  const [list, experiences] = useExperienceSearch({ placeId: place.id, sortBy: 'recommended', limit: 3 })

  return <VerticalSpacer gap={12}>
    {list.fetching && <>
      <LoadingBox style={{ height: rem(100), borderRadius: 4 }} />
      <LoadingBox style={{ height: rem(100), borderRadius: 4 }} />
      <LoadingBox style={{ height: rem(100), borderRadius: 4 }} />
    </>}
    {experiences.map(exp => <>
      {!exp && <LoadingBox style={{ height: rem(100), borderRadius: 4 }} />}
      {exp && <PickItem to={getExperienceOfferPageURL(exp)}>
        <ItemImage
          image={exp.images[0]}
          fit="center"
          height={100}
          width={100}
        />
        <VerticalSpacer gap={8}>
          <BodyText variant="medium" weight="bold" lineClamp={2}>{exp.name}</BodyText>
          <div>
            <EditorPickPriceDisplay price={exp.price} ticketUnitLabel={exp.ticketUnitLabel} />
          </div>
          <PriceRowAgentHubCommission
            kind="plain"
            offerId={exp.id}
            productType={exp.productType}
            offerLocations={[exp.location.name]}
            offer={exp}
          />
        </VerticalSpacer>
      </PickItem>}
    </>)}
  </VerticalSpacer>
}

export default ExperiencesPageEditorsPickList
